/**
 * BatchSheetModal — Printable "Recipe of Seasoning" sheet.
 *
 * iter206 — Layout rewritten to match the physical Tirth Industries sheet
 * (operator photo ref). Notable spec points:
 *  - Issue Date is FROZEN to 02-12-2024 (backend constant).
 *  - Production Date prints as DD-MM-YYYY.
 *  - Batch No row shows `A, B, C, D` plus continuation tag `A260604`.
 *  - Sub-batch grid expands to A-G/A-H/etc. based on sibling count (cap 10).
 *  - Multi-lot rows: same row, LOT NO cell shows "236 (40) / 412 (20)" when
 *    the same material was FIFO-split across lots.
 *  - Section order: BEFORE → DURING → AFTER (each with its own divider row).
 *  - Hierarchy preserved (sorted by recipe_order from the original recipe).
 *  - Customer row added at the top when batch.for_client is set.
 *  - Shift + Line No row added under Start/End Time (acts as a mini timesheet).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Printer, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SECTION_LABELS = {
  premix:          'Material to be added before Production Process',
  during_process:  'Material to be added during Production Process',
  after_process:   'Material to be added after Production Process',
};
const SECTION_ORDER = ['premix', 'during_process', 'after_process'];

export default function BatchSheetModal({ batchId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!batchId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/production/batches/${batchId}/batch-sheet`, { withCredentials: true });
        if (active) setData(r.data);
      } catch (e) {
        if (active) toast.error(e.response?.data?.detail || 'Failed to load batch sheet');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [batchId]);

  const triggerPrint = () => window.print();

  if (!batchId) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-[70] p-4 overflow-y-auto print:bg-white print:p-0 print:static" data-testid="batch-sheet-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-5xl my-4 flex flex-col print:bg-white print:rounded-none print:border-0 print:max-w-none print:m-0 print:w-full">
        {/* Toolbar (hidden on print) */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 print:hidden">
          <div>
            <h3 className="font-heading font-semibold text-zinc-50 text-sm">
              Batch Sheet · {data?.batch?.batch_label_long || batchId}
            </h3>
            <p className="text-zinc-500 text-[10px] mt-0.5">
              FIFO lots already reserved · Print to lock the recipe sheet onto the floor
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={triggerPrint}
              disabled={loading || !data}
              data-testid="print-batch-sheet-btn"
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-1.5 rounded-lg disabled:opacity-50">
              <Printer size={12}/> Print
            </button>
            <button onClick={onClose} data-testid="batch-sheet-close-btn"><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
          </div>
        </div>

        <div className="overflow-x-auto print:overflow-visible">
          {loading && (
            <div className="flex items-center justify-center py-16 text-zinc-500 text-xs"><RefreshCw size={14} className="animate-spin mr-2"/> Loading…</div>
          )}
          {data && <PrintableSheet data={data} />}
        </div>
      </div>

      <style>{`
        @media print {
          /* iter220 — Was hiding EVERYTHING because the modal isn't a direct
             child of <body>; #root wrapped it, so 'body > *:not(modal)' hid
             the app root too → blank sheet on print. Use the standard
             visibility toggle so only the modal's subtree paints. */
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden !important; }
          [data-testid="batch-sheet-modal"], [data-testid="batch-sheet-modal"] * { visibility: visible !important; }
          [data-testid="batch-sheet-modal"] {
            position: absolute !important;
            left: 0 !important; top: 0 !important;
            width: 100% !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            overflow: visible !important;
            z-index: 999999 !important;
          }
          [data-testid="batch-sheet-modal"] > div:first-child { box-shadow: none !important; border: 0 !important; margin: 0 !important; max-width: none !important; width: 100% !important; }
          .batch-sheet-print { color: #111 !important; font-family: 'Times New Roman', serif !important; }
          .batch-sheet-print, .batch-sheet-print * { color: #111 !important; }
          .batch-sheet-print table, .batch-sheet-print th, .batch-sheet-print td { border: 1px solid #111 !important; }
          .batch-sheet-print th, .batch-sheet-print td { padding: 3px 5px !important; }
          @page { size: A4 portrait; margin: 8mm 6mm; }
        }
      `}</style>
    </div>
  );
}

function PrintableSheet({ data }) {
  const { doc, batch, sub_letters, rows } = data;
  const tickCols = sub_letters || [];   // dynamic length 8-10 (iter207)
  const tickColCount = tickCols.length;

  const padRows = (list, minRows) => {
    const padded = [...list];
    while (padded.length < minRows) padded.push({ blank: true });
    return padded;
  };

  const sections = SECTION_ORDER.map(key => ({
    key,
    rows: padRows(rows[key] || [], key === 'premix' ? 8 : 5),
  }));
  const unlottedRows = Object.values(rows || {}).flat().filter(r => (r.lots || []).some(l => l.lot_no === 'UNLOTTED') || r.lot_display === 'UNLOTTED' || (r.lot_display || '').includes('UNLOTTED'));

  return (
    <div className="batch-sheet-print bg-white text-zinc-900 p-5 print:p-2 print:m-0 text-[11px] leading-tight">
      {unlottedRows.length > 0 && (
        <div className="mb-2 border-2 border-dashed border-amber-500 bg-amber-50 text-amber-900 px-3 py-1.5 text-[11px] font-semibold" data-testid="batch-sheet-unlotted-badge">
          LOT ASSIGNMENT PENDING ({unlottedRows.length}): {unlottedRows.map(r => r.material_name).join(', ')} — lot no. to be filled once the purchase lot is entered.
        </div>
      )}
      {/* ── DOC HEADER ────────────────────────────────────── */}
      {/* iter238 — every header row sits on ONE fixed 8-column grid with
          table-layout:fixed, so the vertical borders line up down the page.
          Before this, each row auto-sized its own cells and the borders came
          out ragged on the printed sheet. */}
      <table className="w-full border-collapse text-[11px]" style={{ borderColor: '#111', tableLayout: 'fixed' }}>
        <colgroup>{['14%', '22%', '14%', '14%', '12%', '8%', '8%', '8%'].map((w, i) => <col key={`dh-${i}`} style={{ width: w }} />)}</colgroup>
        <tbody>
          <tr>
            <th className="border border-black px-2 py-1 text-left">Document Name</th>
            <th className="border border-black px-2 py-1 text-left" colSpan={2}>{doc.doc_name}</th>
            <th className="border border-black px-2 py-1 text-center text-lg font-bold" colSpan={5}>{doc.company_name}</th>
          </tr>
          <tr>
            <td className="border border-black px-2 py-1">Issue Date</td>
            <td className="border border-black px-2 py-1 font-mono" colSpan={2}>{doc.issue_date}</td>
            <td className="border border-black px-2 py-1">Version</td>
            <td className="border border-black px-2 py-1 font-bold" colSpan={4}>{doc.version}</td>
          </tr>
          <tr>
            <td className="border border-black px-2 py-1">Next Review Date</td>
            <td className="border border-black px-2 py-1" colSpan={2}>{doc.next_review_date}</td>
            <td className="border border-black px-2 py-1">Document</td>
            <td className="border border-black px-2 py-1 font-bold" colSpan={4}>{doc.doc_no}</td>
          </tr>
        </tbody>
      </table>

      {/* ── BATCH META ─────────────────────────────────────── */}
      {/* Same fixed 8-column grid as the doc header above — the two tables now
          share identical column edges. */}
      <table className="w-full border-collapse text-[11px] mt-1.5" style={{ borderColor: '#111', tableLayout: 'fixed' }}>
        <colgroup>{['14%', '22%', '14%', '14%', '12%', '8%', '8%', '8%'].map((w, i) => <col key={`bm-${i}`} style={{ width: w }} />)}</colgroup>
        <tbody>
          <tr>
            <td className="border border-black px-2 py-1 font-semibold">Product Name</td>
            <td className="border border-black px-2 py-1 break-words">{batch.product_name}</td>
            <td className="border border-black px-2 py-1 font-semibold">Code No.</td>
            <td className="border border-black px-2 py-1 font-mono break-words">{batch.product_code || '—'}</td>
            <td className="border border-black px-2 py-1 font-semibold">Production Date</td>
            <td className="border border-black px-2 py-1 font-mono" colSpan={3}>{batch.production_date_display || '—'}</td>
          </tr>
          {/* Row: Batch No (letters + long tag) */}
          <tr>
            <td className="border border-black px-2 py-1 font-semibold">Batch No.</td>
            <td className="border border-black px-2 py-1 font-mono font-bold text-base break-words" colSpan={7}>
              {batch.batch_label}
              {batch.batch_label_long && (
                <span className="ml-3 text-sm font-semibold text-zinc-700">· {batch.batch_label_long}</span>
              )}
            </td>
          </tr>
          {/* Row: Total Quantity (kg) — right half blank for the operator's
              handwritten adjustment after weighing. */}
          <tr>
            <td className="border border-black px-2 py-1 font-semibold">Total Quantity (Kg)</td>
            <td className="border border-black px-2 py-1 font-mono font-bold">{batch.total_qty_kg}</td>
            <td className="border border-black px-2 py-1 text-center text-[9px] text-zinc-500" colSpan={2}>(manual adjustment)</td>
            <td className="border border-black px-2 py-1" colSpan={4}>&nbsp;</td>
          </tr>
          {/* Row: Start | End | Shift (like a timesheet) */}
          <tr>
            <td className="border border-black px-2 py-1 font-semibold">Start Time</td>
            <td className="border border-black px-2 py-1 font-mono">{batch.start_time || ''}</td>
            <td className="border border-black px-2 py-1 font-semibold">End Time</td>
            <td className="border border-black px-2 py-1 font-mono">{batch.end_time || ''}</td>
            <td className="border border-black px-2 py-1 font-semibold">Shift</td>
            <td className="border border-black px-2 py-1 font-mono" colSpan={3}>{batch.shift || ''}</td>
          </tr>
          <tr>
            <td className="border border-black px-2 py-1 font-semibold">Line No.</td>
            <td className="border border-black px-2 py-1 font-mono">{batch.line_no || ''}</td>
            <td className="border border-black px-2 py-1 font-semibold">Customer</td>
            <td className="border border-black px-2 py-1 font-semibold break-words" colSpan={5}>
              {/* iter207 — Customer shows the client name when a batch is
                  dedicated to a buyer, else the default "General Production". */}
              {batch.for_client || 'General Production'}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── WEIGHING TABLE ─────────────────────────────────── */}
      {/* iter238 — table-layout:fixed makes the colgroup binding, so a long
          multi-lot cell like "3.8 / 2.2" wraps inside its column instead of
          stretching the whole table and knocking the columns out of line. */}
      <table className="w-full border-collapse text-[10px] mt-1.5" style={{ borderColor: '#111', tableLayout: 'fixed' }}>
        {/* VERIFY · LOT QTY · LOT NO · MATERIAL (fills the rest) · TOTAL QTY · A–H ticks.
            Kept on one line: whitespace inside <colgroup> upsets React. */}
        <colgroup><col style={{ width: '5%' }} /><col style={{ width: '8%' }} /><col style={{ width: '11%' }} /><col /><col style={{ width: '9%' }} />{tickCols.map((_, i) => <col key={`col-${i}`} style={{ width: `${Math.max(4, 38 / tickColCount)}%` }} />)}</colgroup>
        <thead>
          <tr>
            <th className="border border-black px-1 py-1 text-center">VERIFY</th>
            <th className="border border-black px-1 py-1 text-center" title="Quantity used from each lot (FIFO traceability)">LOT QTY</th>
            <th className="border border-black px-1 py-1 text-center">LOT NO</th>
            <th className="border border-black px-1 py-1 text-left">{(batch.product_name || '').toUpperCase()}</th>
            <th className="border border-black px-1 py-1 text-center">{batch.total_qty_kg} KG</th>
            {tickCols.map((letter, i) => (
              <th key={`hdr-${i}`} className="border border-black px-1 py-1 text-center font-bold">
                {letter
                  ? <span className="inline-block w-5 h-5 border border-black rounded-full leading-5">{letter}</span>
                  : String.fromCharCode(65 + i)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((sec, secIdx) => (
            <React.Fragment key={sec.key}>
              <tr>
                <td className="border border-black px-1 py-1 text-center font-bold uppercase"
                  colSpan={5 + tickColCount}
                  style={{ background: '#f5f5f5' }}>
                  {SECTION_LABELS[sec.key]}
                </td>
              </tr>
              {sec.rows.map((r, idx) => (
                <tr key={`${sec.key}-${idx}`}>
                  <td className="border border-black px-1 py-1 text-center">&nbsp;</td>
                  <td className="border border-black px-1 py-1 font-mono text-right break-words">{r.blank ? '' : (r.lot_qty_display || '')}</td>
                  <td className="border border-black px-1 py-1 font-mono text-center break-words">{r.blank ? '' : (r.lot_display || '—')}</td>
                  <td className="border border-black px-1 py-1 break-words">{r.blank ? '' : (r.material_name || '')}</td>
                  <td className="border border-black px-1 py-1 font-mono text-right">{r.blank ? '' : r.quantity_total}</td>
                  {tickCols.map((_, ti) => (
                    <td key={`tk-${secIdx}-${idx}-${ti}`} className="border border-black px-1 py-1">&nbsp;</td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
          {/* TOTAL row */}
          <tr>
            <td className="border border-black px-1 py-1 text-center font-bold" colSpan={3}>TOTAL</td>
            <td className="border border-black px-1 py-1">&nbsp;</td>
            <td className="border border-black px-1 py-1 font-mono font-bold text-right">{batch.total_qty_kg}</td>
            {tickCols.map((_, ti) => (
              <td key={`tot-${ti}`} className="border border-black px-1 py-1">&nbsp;</td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* ── SIGNATURE BLOCK ────────────────────────────────── */}
      <table className="w-full border-collapse text-[11px] mt-1.5" style={{ borderColor: '#111' }}>
        <tbody>
          <tr>
            <td className="border border-black px-2 py-2 font-semibold" style={{ width: '22%' }}>Done By (Operator)</td>
            <td className="border border-black px-2 py-2" style={{ width: '28%' }}>
              <div className="text-[10px] text-zinc-500">Name</div>
              <div className="h-5">&nbsp;</div>
              <div className="text-[10px] text-zinc-500 mt-1">Signature</div>
              <div className="h-5">&nbsp;</div>
            </td>
            <td className="border border-black px-2 py-2 font-semibold" style={{ width: '22%' }}>Verified By (Supervisor / QA)</td>
            <td className="border border-black px-2 py-2" style={{ width: '28%' }}>
              <div className="text-[10px] text-zinc-500">Name</div>
              <div className="h-5">&nbsp;</div>
              <div className="text-[10px] text-zinc-500 mt-1">Signature</div>
              <div className="h-5">&nbsp;</div>
            </td>
          </tr>
          <tr>
            <td className="border border-black px-2 py-1.5 font-semibold">Date / Time</td>
            <td className="border border-black px-2 py-1.5">&nbsp;</td>
            <td className="border border-black px-2 py-1.5 font-semibold">Date / Time</td>
            <td className="border border-black px-2 py-1.5">&nbsp;</td>
          </tr>
          <tr>
            <td className="border border-black px-2 py-1.5 font-semibold">Remarks</td>
            <td className="border border-black px-2 py-1.5" colSpan={3}>&nbsp;</td>
          </tr>
        </tbody>
      </table>

      <p className="text-[9px] text-zinc-600 mt-2 italic">
        Printed copy is the master weighing record. Sub-batches: {batch.batch_label || '—'}{batch.batch_label_long ? ` · ${batch.batch_label_long}` : ''} · Lots are FIFO-reserved at planning time and remain locked until QC sign-off. Cancel batch in app to release the lots.
      </p>
    </div>
  );
}
